import { calculateDistance } from '@website/utils/mapUtils';

import { AddressGeocodeError, type ResolveAddressInput, resolveAddressCoordinates } from './resolveCoordinates';

/**
 * The at_home base point + travel radius (in KM) a typed client address must fall
 * within. Supply it only when the booked location is at_home AND exposes both a
 * base point and a positive radius; otherwise leave it undefined to skip the
 * range check (venue/online, or a legacy location with no radius).
 */
export interface BookingAddressRadius {
  latitude: number;
  longitude: number;
  /** Service radius in KILOMETRES (already converted from any metres source). */
  radiusKm: number;
}

export type ResolveBookingAddressResult =
  | { ok: true; latitude: number; longitude: number }
  | { ok: false; reason: 'geocode_failed' }
  | { ok: false; reason: 'out_of_range'; distanceKm: number; maxRadiusKm: number }
  | { ok: false; reason: 'unexpected' };

/**
 * Geocode a pro-typed at_home client address and, when a radius is supplied,
 * block addresses outside the professional's service area BEFORE the booking
 * call. This is the single source of truth for the at_home geocode-and-block
 * gate shared by every pro booking surface (take flow, direct-booking dialog,
 * book-occurrence form), so a future surface cannot silently omit the
 * coordinates the backend's `validateAddressInRadius` requires.
 *
 * Returns a discriminated result the caller maps to its own translated copy
 * (mirrors `Booking.errors.*`): `out_of_range` carries the distance + max radius
 * for the message, `geocode_failed` means the address could not be located, and
 * `unexpected` is any other failure (network, etc.). Never throws.
 */
export async function resolveBookingAddressCoordinates(
  input: ResolveAddressInput,
  radius?: BookingAddressRadius
): Promise<ResolveBookingAddressResult> {
  let coords: { latitude: number; longitude: number };
  try {
    coords = await resolveAddressCoordinates(input);
  } catch (err) {
    if (err instanceof AddressGeocodeError) {
      return { ok: false, reason: 'geocode_failed' };
    }
    return { ok: false, reason: 'unexpected' };
  }

  if (radius && radius.radiusKm > 0) {
    const distanceKm = calculateDistance(radius.latitude, radius.longitude, coords.latitude, coords.longitude);
    if (distanceKm > radius.radiusKm) {
      return {
        ok: false,
        reason: 'out_of_range',
        distanceKm: Math.round(distanceKm * 10) / 10,
        maxRadiusKm: radius.radiusKm
      };
    }
  }

  return { ok: true, latitude: coords.latitude, longitude: coords.longitude };
}
